import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import { TopBar } from '@/layout/TopBar';
import { Sidebar } from '@/layout/Sidebar';
import { ErrorFallback } from '@/components/common/ErrorFallback';
import { VideoPlayer } from '@/components/player/VideoPlayer';
import { PlayerInfo } from '@/components/player/PlayerInfo';
import { HomePage } from '@/pages/HomePage';
import { WorldCupPage } from '@/pages/WorldCupPage';
import { WorldCupMatchesPage } from '@/pages/WorldCupMatchesPage';
import { TopSportsPage } from '@/pages/TopSportsPage';
import { SportCategoryPage } from '@/pages/SportCategoryPage';
import { CountryPage } from '@/pages/CountryPage';
import { AllChannelsPage } from '@/pages/AllChannelsPage';
import { SearchPage } from '@/pages/SearchPage';
import { FavoritesPage } from '@/pages/FavoritesPage';
import { RecentPage } from '@/pages/RecentPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { DevChannelsPage } from '@/pages/DevChannelsPage';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useRecentsStore } from '@/store/useRecentsStore';
import { useKeyboardShortcuts } from '@/components/player/useKeyboardShortcuts';
import { KeyboardHelp } from '@/components/player/KeyboardHelp';

function ErrorBoundary({ children }: { children: ReactNode }) {
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handler = (e: ErrorEvent) => {
      setError(e.error ?? new Error(e.message));
    };
    const rejectionHandler = (e: PromiseRejectionEvent) => {
      setError(e.reason instanceof Error ? e.reason : new Error(String(e.reason)));
    };
    window.addEventListener('error', handler);
    window.addEventListener('unhandledrejection', rejectionHandler);
    return () => {
      window.removeEventListener('error', handler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);

  if (error) {
    return <ErrorFallback error={error} onReset={() => setError(null)} />;
  }

  return <>{children}</>;
}

const ROUTES = (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/matches" element={<WorldCupMatchesPage />} />
    <Route path="/world-cup" element={<WorldCupPage />} />
    <Route path="/top-sports" element={<TopSportsPage />} />
    <Route path="/sport/:subcategory" element={<SportCategoryPage />} />
    <Route path="/country/:code" element={<CountryPage />} />
    <Route path="/all-channels" element={<AllChannelsPage />} />
    <Route path="/search" element={<SearchPage />} />
    <Route path="/favorites" element={<FavoritesPage />} />
    <Route path="/recent" element={<RecentPage />} />
    <Route path="/settings" element={<SettingsPage />} />
    {import.meta.env.DEV && (
      <Route path="/dev/channels" element={<DevChannelsPage />} />
    )}
  </Routes>
);

export default function App() {
  const loadInitial = useChannelsStore((s) => s.loadInitial);
  const isInitialLoaded = useChannelsStore((s) => s.isInitialLoaded);
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const { showHelp, setShowHelp } = useKeyboardShortcuts();
  const currentChannel = usePlayerStore((s) => s.currentChannel);
  const location = useLocation();

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Track recents: every time the user switches channels, push the
  // new id to the recents store. We subscribe imperatively so this
  // works for changes that happen outside React (e.g. keyboard
  // shortcuts, programmatic navigation from the search page).
  useEffect(() => {
    return usePlayerStore.subscribe((state, prev) => {
      if (state.currentChannel && state.currentChannel.id !== prev.currentChannel?.id) {
        useRecentsStore.getState().addRecent(state.currentChannel.id);
      }
    });
  }, []);

  // Clear player when navigating to a different page (unless locked).
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      const { currentChannel, playerLocked, setCurrentChannel } = usePlayerStore.getState();
      if (currentChannel && !playerLocked) {
        setCurrentChannel(null);
      }
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);

  // Deep-link: if URL has ?channel=ID, auto-select that channel once
  // the channel data is loaded. Cleans up the URL so a refresh doesn't
  // re-trigger the same selection.
  useEffect(() => {
    if (!isInitialLoaded) return;

    const params = new URLSearchParams(window.location.search);
    const channelId = params.get('channel');
    if (!channelId) return;

    const channel = sportsChannels.find((c) => c.id === channelId);
    if (channel) {
      usePlayerStore.getState().setCurrentChannel(channel);
    }

    // Remove the query param from the URL bar.
    const url = new URL(window.location.href);
    url.searchParams.delete('channel');
    window.history.replaceState({}, '', url.toString());
  }, [isInitialLoaded, sportsChannels]);

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-bg text-text overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar />
          <div className="flex-1 flex overflow-hidden">
            {/* ── Video player (center) — only when a channel is playing ── */}
            {currentChannel && (
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Video — fixed at top, keeps its aspect ratio */}
                <div className="flex-shrink-0">
                  <VideoPlayer channel={currentChannel} />
                </div>
                {/* Channel info / actions / EPG / errors — scrolls */}
                <div className="flex-1 overflow-y-auto">
                  <PlayerInfo channel={currentChannel} />
                </div>
              </div>
            )}

            {/* ── Routes: center when idle, right panel when playing ── */}
            {/*
              The <div> is always in the tree so Routes stay mounted across
              state transitions. CSS classes swap to reflow the routes
              content between the center (flex-1) and the right-side panel
              (w-80).
            */}
            <div
              key={location.pathname}
              className={`overflow-y-auto ${currentChannel
                  ? 'hidden lg:block lg:w-96 lg:flex-shrink-0 lg:border-l lg:border-border bg-bg @container p-3'
                  : 'flex-1 p-4 lg:p-6 @container'
                }`}
            >
              {ROUTES}
            </div>

            {/* No right placeholder when idle — routes panel uses full center space */}
          </div>
        </div>
        <KeyboardHelp open={showHelp} onClose={() => setShowHelp(false)} />
      </div>
    </ErrorBoundary>
  );
}